import { Button } from '@/components/ui/button.tsx';
import { Card } from '@/components/ui/card.tsx';
import { Separator } from '@/components/ui/separator.tsx';
import { useAuth } from '@/contexts/AuthContext.tsx';
import { Marque } from '@/components/Marque.tsx';
import { CredentialsLoginForm } from '@/components/CredentialsLoginForm.tsx';

/**
 * Wellspring sign-in. Brand-aligned per the Metron Design System:
 *   - No purple/blue gradients (explicitly banned by the brand guide).
 *   - Marque + Audiowide "METRON" eyebrow + Titillium product wordmark.
 *   - Surface is a deep brand panel on the left (Metron Blue dark 2 → Metron
 *     Blue), with the sign-in form on a clean white surface on the right.
 */
export function CustomSSOLogin() {
  const { login } = useAuth();

  const handleSSOLogin = () => {
    // Use Amplify's signInWithRedirect (via AuthContext) to properly initiate
    // OAuth flow with PKCE + state parameters for security.
    login();
  };

  return (
    <div className='min-h-screen flex flex-col lg:flex-row bg-background'>
      {
        /* Left rail — Metron brand panel with marque watermark.
          The brand guide explicitly endorses a solid-color brand block and
          oversized marque watermark running off at least one edge. */
      }
      <aside
        className='relative hidden lg:flex flex-col justify-between overflow-hidden p-12 text-white'
        style={{
          background:
            'linear-gradient(155deg, var(--metron-blue-dark-2) 0%, var(--metron-blue) 100%)',
        }}
      >
        <div className='relative z-10 flex items-center gap-3'>
          <Marque size={40} color='var(--metron-blue-2)' />
          <span className='font-display text-sm tracking-[0.14em]'>METRON</span>
        </div>

        <div className='relative z-10 max-w-md'>
          <p className='font-display text-xs tracking-[0.18em] text-white/70 mb-3'>
            WELLSPRING
          </p>
          <h1 className='font-heading text-4xl font-light leading-tight'>
            Smart water management,<br />
            <span className='font-semibold'>made operational.</span>
          </h1>
          <p className='mt-5 max-w-sm text-sm leading-relaxed text-white/80'>
            One-minute-interval insights, fixture-level usage detection, and portfolio-wide alerts —
            all in a single internal control plane.
          </p>
        </div>

        <p className='relative z-10 text-xs text-white/60'>
          Metron Water Intelligence · 30+ years of water expertise
        </p>

        {
          /* Oversized marque watermark — brand-guide pattern: run off at least
            one edge, ~14% opacity, in a brand tint. */
        }
        <Marque
          size={620}
          color='rgba(107,205,255,0.14)'
          className='absolute -right-44 -bottom-32 pointer-events-none select-none'
        />
      </aside>

      {/* Form column */}
      <main className='flex-1 flex items-center justify-center p-6'>
        <Card className='w-full max-w-md p-10 shadow-sm border-border'>
          {/* Mobile brand block — visible only when the left rail collapses */}
          <div className='lg:hidden mb-8 flex items-center gap-3 text-primary'>
            <Marque size={32} />
            <div className='flex flex-col leading-none'>
              <span className='metron-display-eyebrow'>METRON</span>
              <span className='metron-wordmark text-lg text-foreground mt-1'>
                Wellspring
              </span>
            </div>
          </div>

          <div className='space-y-1.5'>
            <p className='metron-eyebrow'>Sign in</p>
            <h2 className='font-heading text-2xl font-normal tracking-tight'>
              Welcome to Wellspring
            </h2>
            <p className='text-sm text-muted-foreground'>
              All Metron employees should sign in with Microsoft.
            </p>
          </div>

          <Button
            onClick={handleSSOLogin}
            size='lg'
            className='mt-8 w-full bg-primary hover:bg-[var(--metron-blue-dark-1)] text-primary-foreground'
          >
            <svg
              className='w-5 h-5 mr-2'
              viewBox='0 0 21 21'
              xmlns='http://www.w3.org/2000/svg'
              aria-hidden='true'
            >
              <rect x='1' y='1' width='9' height='9' fill='#f25022' />
              <rect x='11' y='1' width='9' height='9' fill='#00a4ef' />
              <rect x='1' y='11' width='9' height='9' fill='#7fba00' />
              <rect x='11' y='11' width='9' height='9' fill='#ffb900' />
            </svg>
            Continue with Microsoft
          </Button>

          <div className='my-6 flex items-center gap-3'>
            <Separator className='flex-1' />
            <span className='text-xs text-muted-foreground'>or</span>
            <Separator className='flex-1' />
          </div>

          <p className='mb-4 text-sm text-muted-foreground'>
            All other users, sign in with your email and password.
          </p>

          <CredentialsLoginForm />

          <p className='mt-6 text-xs text-muted-foreground text-center'>
            By signing in, you agree to Metron's Terms of Service and Privacy Policy.
          </p>
        </Card>
      </main>
    </div>
  );
}
